// src/pages/admin/analytics/components/ManualWastageLogTab.jsx
import { useState } from 'react';
import {
  Plus,
  Trash2,
  Calendar,
  User,
  Package,
  AlertTriangle,
  RefreshCw,
  Filter,
  FileText,
  Edit2,
  CheckSquare,
  Square,
  Layers,
} from 'lucide-react';
import toast from 'react-hot-toast';
import analyticsService from '../../../../services/analyticsService';
import EditWastageModal from './EditWastageModal';
import BulkWastageEditModal from './BulkWastageEditModal';
import DeleteWastageModal from './DeleteWastageModal';

const ManualWastageLogTab = ({
  logs = [],
  summary = {},
  loading,
  onOpenAddModal,
  onRefresh,
}) => {
  const [reasonFilter, setReasonFilter] = useState('ALL');
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingLog, setEditingLog] = useState(null);
  const [deletingLog, setDeletingLog] = useState(null);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <RefreshCw className="h-8 w-8 animate-spin text-rose-500 mr-3" />
        <span className="font-medium text-lg">Loading Manual Wastage Logs...</span>
      </div>
    );
  }

  const filteredLogs = logs.filter((log) => {
    if (reasonFilter !== 'ALL' && log.reason !== reasonFilter) return false;
    return true;
  });

  const getReasonBadge = (reason) => {
    switch (reason) {
      case 'trimming_prep':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-800">
            Chicken/Prep Loss
          </span>
        );
      case 'expiration':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-800">
            Expired Product
          </span>
        );
      case 'spoilage':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-800">
            Spoilage
          </span>
        );
      case 'damaged':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
            Damaged
          </span>
        );
      case 'quality_rejection':
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-800">
            Quality Rejection
          </span>
        );
      default:
        return (
          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-800">
            {reason}
          </span>
        );
    }
  };

  // Selection handlers
  const handleToggleSelectAll = () => {
    if (selectedIds.length === filteredLogs.length && filteredLogs.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredLogs.map((l) => l._id));
    }
  };

  const handleToggleSelectRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  // Selected logs objects
  const selectedLogs = logs.filter((l) => selectedIds.includes(l._id));

  return (
    <div className="space-y-6">
      {/* Header with Log New Wastage Button */}
      <div className="bg-gradient-to-r from-red-700 via-rose-700 to-orange-700 text-white rounded-xl p-6 shadow-lg flex flex-col md:flex-row items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-black">Manual Wastage & Expiration Log</h2>
          <p className="text-xs text-rose-100 mt-1">
            Log preparation waste (e.g., chicken trimming loss upon delivery, milk spillage) and expired products to automatically update stock & financial net profit calculations.
          </p>
        </div>
        <div className="flex items-center space-x-3 flex-shrink-0">
          <button
            onClick={onOpenAddModal}
            className="px-5 py-3 bg-white text-rose-700 hover:bg-rose-50 font-bold rounded-xl shadow-md hover:shadow-xl transition-all flex items-center space-x-2"
          >
            <Plus className="h-5 w-5 text-rose-600" />
            <span>+ Log New Wastage</span>
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase">Total Logged Wastage Cost</p>
          <p className="text-2xl font-black text-rose-700 mt-1">
            Rs. {(summary.totalWastageCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-gray-500 mt-1">{logs.length} logged wastage record(s)</p>
        </div>

        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase">Raw Material Prep Loss</p>
          <p className="text-2xl font-black text-orange-600 mt-1">
            Rs.{' '}
            {(
              summary.byType?.find((t) => t._id === 'raw_material')?.totalCost || 0
            ).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-gray-500 mt-1">Chicken trimming, peeling, spillage</p>
        </div>

        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
          <p className="text-xs font-semibold text-gray-500 uppercase">Finished Product Expirations</p>
          <p className="text-2xl font-black text-red-600 mt-1">
            Rs.{' '}
            {(
              summary.byType?.find((t) => t._id === 'product')?.totalCost || 0
            ).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-gray-500 mt-1">Expired menu items & damages</p>
        </div>
      </div>

      {/* Filter Bar & Bulk Actions Bar */}
      <div className="space-y-3">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400 mr-1" />
            <span className="text-xs font-bold text-gray-600 uppercase">Filter Reason:</span>
            <button
              onClick={() => setReasonFilter('ALL')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                reasonFilter === 'ALL' ? 'bg-gray-900 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All Logs ({logs.length})
            </button>
            <button
              onClick={() => setReasonFilter('trimming_prep')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                reasonFilter === 'trimming_prep' ? 'bg-orange-600 text-white shadow-sm' : 'bg-orange-50 text-orange-700 hover:bg-orange-100'
              }`}
            >
              Chicken/Prep Loss
            </button>
            <button
              onClick={() => setReasonFilter('expiration')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                reasonFilter === 'expiration' ? 'bg-red-600 text-white shadow-sm' : 'bg-red-50 text-red-700 hover:bg-red-100'
              }`}
            >
              Expirations
            </button>
            <button
              onClick={() => setReasonFilter('spoilage')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                reasonFilter === 'spoilage' ? 'bg-purple-600 text-white shadow-sm' : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
              }`}
            >
              Spoilage
            </button>
            <button
              onClick={() => setReasonFilter('damaged')}
              className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                reasonFilter === 'damaged' ? 'bg-amber-600 text-white shadow-sm' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
              }`}
            >
              Damaged
            </button>
          </div>

          {/* Quick status on right side */}
          {selectedIds.length === 0 && (
            <div className="text-xs text-gray-400 font-medium">
              Select rows to use bulk edit or bulk delete
            </div>
          )}
        </div>

        {/* Floating / Highlighted Bulk Actions Banner */}
        {selectedIds.length > 0 && (
          <div className="bg-gradient-to-r from-rose-50 to-orange-50 border-2 border-rose-300 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-md animate-in fade-in slide-in-from-top-2 duration-200">
            <div className="flex items-center space-x-3">
              <span className="px-3 py-1 bg-rose-600 text-white rounded-lg text-xs font-black tracking-wide shadow-sm">
                {selectedIds.length} Selected
              </span>
              <span className="text-xs font-bold text-gray-800">
                Wastage {selectedIds.length === 1 ? 'record' : 'records'} ready for bulk action
              </span>
            </div>

            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setIsBulkModalOpen(true)}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow transition-all flex items-center space-x-2"
              >
                <Edit2 className="h-3.5 w-3.5" />
                <span>Bulk Edit ({selectedIds.length})</span>
              </button>
              <button
                type="button"
                onClick={() => setIsBulkDeleteOpen(true)}
                className="px-3.5 py-2 bg-white hover:bg-red-50 text-red-700 text-xs font-bold rounded-xl border border-red-200 transition-all flex items-center space-x-1.5"
              >
                <Trash2 className="h-3.5 w-3.5 text-red-600" />
                <span>Bulk Delete</span>
              </button>
              <button
                type="button"
                onClick={handleClearSelection}
                className="px-3 py-2 bg-white hover:bg-gray-100 text-gray-700 text-xs font-bold rounded-xl border border-gray-300 transition-all"
              >
                Deselect All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Wastage Logs Table */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold uppercase tracking-wider">
              <tr>
                <th className="p-4 w-10 text-center">
                  <input
                    type="checkbox"
                    checked={filteredLogs.length > 0 && selectedIds.length === filteredLogs.length}
                    onChange={handleToggleSelectAll}
                    disabled={filteredLogs.length === 0}
                    className="w-4 h-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                    title="Select all"
                  />
                </th>
                <th className="p-4">Date & Time</th>
                <th className="p-4">Branch</th>
                <th className="p-4">Item Type</th>
                <th className="p-4">Item Name</th>
                <th className="p-4 text-center">Wastage Quantity</th>
                <th className="p-4 text-right">Unit Cost</th>
                <th className="p-4 text-right">Total Wastage Cost</th>
                <th className="p-4 text-center">Reason</th>
                <th className="p-4">Notes / Staff Remarks</th>
                <th className="p-4">Logged By</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-medium">
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan="12" className="p-8 text-center text-gray-500">
                    No manual wastage entries logged yet. Click "+ Log New Wastage" to create one.
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => {
                  const isRM = log.itemType === 'raw_material';
                  const itemName = isRM ? log.rawMaterial?.name : log.product?.name;
                  const itemCode = isRM ? log.rawMaterial?.code : log.product?.sku;
                  const isSelected = selectedIds.includes(log._id);

                  return (
                    <tr
                      key={log._id}
                      className={`transition-colors ${
                        isSelected ? 'bg-rose-50/50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="p-4 text-center">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectRow(log._id)}
                          className="w-4 h-4 rounded border-gray-300 text-rose-600 focus:ring-rose-500 cursor-pointer"
                        />
                      </td>
                      <td className="p-4 text-gray-600 whitespace-nowrap">
                        {new Date(log.createdAt).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className="p-4 font-bold text-gray-800">{log.branch?.name || 'N/A'}</td>
                      <td className="p-4">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                            isRM ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {isRM ? 'Raw Material' : 'Product'}
                        </span>
                      </td>
                      <td className="p-4">
                        <p className="font-bold text-gray-900">{itemName || 'Unknown'}</p>
                        {itemCode && <p className="text-[10px] text-gray-500">Code/SKU: {itemCode}</p>}
                      </td>
                      <td className="p-4 text-center font-black text-rose-700 whitespace-nowrap">
                        {log.quantity} {log.unitName || 'units'}
                      </td>
                      <td className="p-4 text-right text-gray-600 whitespace-nowrap">
                        Rs. {(log.unitCost || 0).toFixed(2)}
                      </td>
                      <td className="p-4 text-right font-black text-gray-900 whitespace-nowrap">
                        Rs. {(log.totalCost || 0).toFixed(2)}
                      </td>
                      <td className="p-4 text-center whitespace-nowrap">{getReasonBadge(log.reason)}</td>
                      <td className="p-4 text-gray-600 max-w-xs truncate" title={log.notes}>
                        {log.notes || '-'}
                      </td>
                      <td className="p-4 text-gray-700 font-medium whitespace-nowrap">
                        {log.loggedBy?.name || log.loggedBy?.username || 'System'}
                      </td>
                      <td className="p-4 text-center whitespace-nowrap">
                        <div className="flex items-center justify-center space-x-1.5">
                          <button
                            type="button"
                            onClick={() => setEditingLog(log)}
                            title="Edit wastage entry"
                            className="p-1.5 text-gray-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeletingLog(log)}
                            title="Delete wastage entry and restore stock"
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Single Wastage Modal */}
      <EditWastageModal
        isOpen={Boolean(editingLog)}
        onClose={() => setEditingLog(null)}
        onSuccess={onRefresh}
        log={editingLog}
      />

      {/* Bulk Edit Wastage Modal */}
      <BulkWastageEditModal
        isOpen={isBulkModalOpen}
        onClose={() => setIsBulkModalOpen(false)}
        onSuccess={onRefresh}
        selectedLogs={selectedLogs}
        onClearSelection={handleClearSelection}
      />

      {/* Delete Single Wastage Confirmation Modal */}
      <DeleteWastageModal
        isOpen={Boolean(deletingLog)}
        onClose={() => setDeletingLog(null)}
        onSuccess={onRefresh}
        log={deletingLog}
      />

      {/* Bulk Delete Wastage Confirmation Modal */}
      <DeleteWastageModal
        isOpen={isBulkDeleteOpen}
        onClose={() => setIsBulkDeleteOpen(false)}
        onSuccess={onRefresh}
        bulkLogs={selectedLogs}
        onClearSelection={handleClearSelection}
      />
    </div>
  );
};

export default ManualWastageLogTab;
